'use client';

import { MediaWithMaskBg } from '@/app/components/shared/MediaWithMaskBg';
import type { ImagePrompt, VideoPrompt } from '@/app/types';
import { cn } from '@/app/utils';

export interface MediaPromptPreviewProps {
  /**
   * 图片或视频提示数据
   */
  prompt: ImagePrompt | VideoPrompt | null;
  /**
   * 点击事件
   */
  onClick?: () => void;
  /**
   * 自定义类名
   */
  className?: string;
}

/**
 * 媒体提示预览组件
 * 用于展示 ImagePrompt 或 VideoPrompt 的第一个预览
 * - 图片：展示第一个图片（selectedImagePreview 或 sourceImagePreview）
 * - 视频：自动播放视频（video 或 videoPreview），循环播放，静音
 */
export const MediaPromptPreview = ({ prompt, onClick, className }: MediaPromptPreviewProps) => {
  if (!prompt) {
    return null;
  }

  // 判断是否为视频类型
  const isVideo = 'video' in prompt;

  if (isVideo) {
    const videoPrompt = prompt as VideoPrompt;
    const videoSrc = videoPrompt.videoPreview || videoPrompt.video;
    const coverImage = videoPrompt.coverImagePreview || videoPrompt.coverImage;

    return (
      <div className={cn('relative h-full w-full overflow-hidden rounded-8', className)} onClick={onClick}>
        <MediaWithMaskBg
          src={videoSrc}
          maskSrc={coverImage}
          videoCoverImage={coverImage}
          type="video"
          disableDrag={true}
        />
      </div>
    );
  }

  // 图片类型
  const imagePrompt = prompt as ImagePrompt;
  const imageUrl = imagePrompt.selectedImagePreview || imagePrompt.sourceImagePreview;

  if (!imageUrl) {
    return null;
  }

  return (
    <MediaWithMaskBg
      src={imageUrl}
      maskSrc={imageUrl}
      alt="image preview"
      useCover={false}
      className={cn('overflow-hidden rounded-8', className)}
      onClick={onClick}
    />
  );
};
